import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { ECOM_ORDER_STATUS } from '@/modules/ecom-policy/constants'
import { getStatusName } from '@/modules/ecom-policy/helpers'
import queries from '@/modules/ecom-policy/queries'
import ecomPolicyResources from '@/modules/ecom-policy/resources'

import type { PolicyOrder } from '../types'

import EcomPolicyOrderListItem from './EcomPolicyOrderListItem'

const EcomPolicyOrderList: FC<{
  searchFilter: string
  statusFilter: number
}> = ({ searchFilter, statusFilter }) => {
  const translate = useTranslation()
  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<keyof PolicyOrder>()

  const { data: orderList = [], isLoading } = useQuery(
    queries.policyOrderList(),
    ecomPolicyResources.getOrderList
  )
  const stringMatcher = (order: PolicyOrder) => [
    order.id,
    order.productNames,
    order.name,
    order.policyName,
    order.customerName,
    order.vendorSalesOrderNo,
    order.departmentName,
    translate(`ecom_order_status/${getStatusName(order.statusId)}`),
    order.productSkus,
  ]

  const getFilteredList = () => {
    const newStatusCategory = [
      ECOM_ORDER_STATUS.NEW,
      ECOM_ORDER_STATUS.ACCEPTED,
      ECOM_ORDER_STATUS.CONFIRMED,
    ]
    if (!searchFilter && statusFilter === ECOM_ORDER_STATUS.ALL) return orderList

    if (statusFilter === ECOM_ORDER_STATUS.ALL) {
      return orderList.filter(getQueryFilter(searchFilter, stringMatcher))
    }

    if (statusFilter === ECOM_ORDER_STATUS.NEW) {
      return orderList.filter(
        order =>
          newStatusCategory.includes(order.statusId) &&
          getQueryFilter(searchFilter, stringMatcher)(order)
      )
    }

    return orderList.filter(
      order => order.statusId === statusFilter && getQueryFilter(searchFilter, stringMatcher)(order)
    )
  }

  const itemsFiltered = getFilteredList()
  const sortedList = getSortedList({ list: itemsFiltered, querySort, querySortBy, locale })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)

  if (isLoading) return <LoaderCircle />

  if (!visibleList || visibleList.length === 0)
    return (
      <NoResults>
        <Translation id="No orders found" />
      </NoResults>
    )

  return (
    <>
      <ResultCount count={itemsFiltered.length} />
      <Table rowHeight="tall">
        <thead>
          <tr>
            <Th id="name" onClick={handleSort}>
              <Translation id="user" />
            </Th>
            <Th id="departmentName" onClick={handleSort}>
              <Translation id="company" />
            </Th>
            <Th>
              <Translation id="products" />
            </Th>
            <Th id="orderDate" onClick={handleSort}>
              <Translation id="order date" />
            </Th>
            <Th id="status" onClick={handleSort}>
              <Translation id="status" />
            </Th>
            <Th id="cost" onClick={handleSort} className="cost">
              <Translation id="cost" />
            </Th>
          </tr>
        </thead>

        <tbody>
          {visibleList.map(item => (
            <EcomPolicyOrderListItem key={item.id} searchFilter={searchFilter} {...item} />
          ))}
        </tbody>
      </Table>

      <ListLoaderComponent />
    </>
  )
}

export default EcomPolicyOrderList
